import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/pl";
import {
    Box, HStack, VStack, Text, SimpleGrid, Button, Skeleton, SkeletonCircle,
} from "@chakra-ui/react";
import {
    HiOutlineBriefcase, HiOutlineCalendarDays, HiOutlineClock, HiOutlineStar,
    HiOutlinePlus, HiOutlineChevronRight,
} from "react-icons/hi2";
import useSetPageHeader from "@/components/hooks/useSetPageHeader";
import { useAuth } from "@/auth/AuthContext";
import { useAgentTypes } from "@/components/layout/AgentTypesContext";
import { useSalesDashboardData } from "@/components/layout/SalesDashboardDataContext";
import NewCampaignModal from "./NewCampaignModal";

dayjs.extend(relativeTime);
dayjs.locale("pl");

const CAMPAIGNS_TO_DISPLAY = 4;
const LEADS_TO_DISPLAY = 7;
const NEW_LEAD_WINDOW_DAYS = 7;

// Soft pastel pills matching the rest of the app -- kept local like the equivalent
// candidateStatus map, since LeadStatus is its own vocabulary (see backend LeadStatus.cs).
const LEAD_STATUS_STYLES = {
    new: { bg: "#E1EEFD", color: "#1D4ED8" },
    contactattempted: { bg: "#EAEAF2", color: "#65616F" },
    contacted: { bg: "#E1EEFD", color: "#1D4ED8" },
    conversation: { bg: "#F1E8FE", color: "#6D28D9" },
    qualifying: { bg: "#FDEFDC", color: "#B45309" },
    qualified: { bg: "#E1F7FA", color: "#0E7C86" },
    interested: { bg: "#E1F7FA", color: "#0E7C86" },
    conversionproposed: { bg: "#F1E8FE", color: "#6D28D9" },
    converted: { bg: "#E4F7EC", color: "#178A4C" },
    notinterested: { bg: "#EAEAF2", color: "#65616F" },
    noresponse: { bg: "#EAEAF2", color: "#65616F" },
    disqualified: { bg: "#FCE8EC", color: "#C81E3A" },
    handedtohuman: { bg: "#FDEFDC", color: "#B45309" },
};

const LeadStatusPill = ({ status, t }) => {
    const key = status?.toLowerCase() ?? "new";
    const style = LEAD_STATUS_STYLES[key] ?? LEAD_STATUS_STYLES.new;
    return (
        <Text as="span" bg={style.bg} color={style.color} fontSize="10.5px" fontWeight={600} px={2.5} py={0.5} borderRadius="999px" whiteSpace="nowrap" flexShrink={0}>
            {t(`leadStatus.${key}`)}
        </Text>
    );
};

const timeAgo = (date) => dayjs(date).fromNow();

const initialsOf = (name) =>
    (name ?? "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

const fullNameOf = (lead, fallback) =>
    [lead.firstname, lead.lastname].filter(Boolean).join(" ") || lead.company || fallback;

const StatCard = ({ label, value, icon, iconBg, iconColor }) => (
    <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={4}>
        <HStack gap={3} align="center">
            <Box w="36px" h="36px" borderRadius={10} bg={iconBg} color={iconColor} display="flex" alignItems="center" justifyContent="center" fontSize="18px" flexShrink={0}>
                {icon}
            </Box>
            <VStack align="flex-start" gap={0} minW={0}>
                <Text color="gray.900" fontSize="xl" fontWeight={700} lineHeight={1.1}>{value}</Text>
                <Text color="gray.500" fontSize="xs" truncate>{label}</Text>
            </VStack>
        </HStack>
    </Box>
);

const LeadRow = ({ lead, campaign, onClick, t, tSales }) => (
    <HStack
        w="full"
        justify="space-between"
        gap={3}
        px={4}
        py={2.5}
        borderBottom="1px solid"
        borderColor="gray.100"
        cursor="pointer"
        _hover={{ bg: "gray.50" }}
        onClick={onClick}
    >
        <HStack gap={3} minW={0}>
            <Box w="34px" h="34px" borderRadius="full" bg="gray.100" color="gray.600" display="flex" alignItems="center" justifyContent="center" fontWeight={700} fontSize="xs" flexShrink={0}>
                {initialsOf(fullNameOf(lead, "??"))}
            </Box>
            <VStack align="flex-start" gap={0} minW={0}>
                <Text color="gray.900" fontSize="sm" fontWeight={600} truncate>
                    {fullNameOf(lead, tSales("unnamedLead"))}
                </Text>
                <Text color="gray.400" fontSize="xs" truncate>
                    {campaign.name} · {timeAgo(lead.createdAt)}
                </Text>
            </VStack>
        </HStack>
        <LeadStatusPill status={lead.status} t={t} />
    </HStack>
);

const CampaignRow = ({ campaign, onClick, t }) => (
    <HStack
        w="full"
        justify="space-between"
        gap={2}
        px={4}
        py={2}
        borderBottom="1px solid"
        borderColor="gray.100"
        cursor="pointer"
        _hover={{ bg: "gray.50" }}
        onClick={onClick}
    >
        <Text color="gray.800" fontSize="sm" fontWeight={500} truncate minW={0} flex={1}>
            {campaign.name}
        </Text>
        <HStack gap={2} flexShrink={0}>
            <Text color="gray.400" fontSize="xs" whiteSpace="nowrap">
                {campaign.leadCount ?? "—"} {t("leadsSuffix")}
            </Text>
            <HiOutlineChevronRight size={14} color="#9A96A6" />
        </HStack>
    </HStack>
);

const DashboardSkeleton = () => (
    <VStack w="full" py={6} gap={4} align="flex-start">
        <HStack w="full" justify="space-between" align="flex-start">
            <VStack align="flex-start" gap={2}>
                <Skeleton height="28px" width="220px" borderRadius={6} />
                <Skeleton height="16px" width="280px" borderRadius={6} />
            </VStack>
            <Skeleton height="40px" width="160px" borderRadius={8} />
        </HStack>

        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={4} w="full">
            {Array.from({ length: 4 }).map((_, i) => (
                <Box key={i} w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={4}>
                    <HStack gap={3}>
                        <Skeleton width="36px" height="36px" borderRadius={10} />
                        <VStack align="flex-start" gap={2} flex={1}>
                            <Skeleton height="18px" width="40%" borderRadius={4} />
                            <Skeleton height="12px" width="70%" borderRadius={4} />
                        </VStack>
                    </HStack>
                </Box>
            ))}
        </SimpleGrid>

        <HStack w="full" align="flex-start" gap={4} flexWrap={{ base: "wrap", lg: "nowrap" }}>
            <Box flex="3" minW={{ base: "full", lg: "0" }} bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={4}>
                <Skeleton height="18px" width="160px" borderRadius={4} mb={4} />
                <VStack align="stretch" gap={4}>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <HStack key={i} gap={3}>
                            <SkeletonCircle size="34px" />
                            <VStack align="flex-start" gap={2} flex={1}>
                                <Skeleton height="14px" width="45%" borderRadius={4} />
                                <Skeleton height="10px" width="65%" borderRadius={4} />
                            </VStack>
                        </HStack>
                    ))}
                </VStack>
            </Box>

            <VStack flex="2" minW={{ base: "full", lg: "260px" }} gap={4} align="stretch">
                <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={4}>
                    <Skeleton height="16px" width="140px" borderRadius={4} mb={3} />
                    <VStack align="stretch" gap={3}>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} height="14px" width="100%" borderRadius={4} />
                        ))}
                    </VStack>
                </Box>
            </VStack>
        </HStack>
    </VStack>
);

const SalesDashboardPage = () => {
    const { t } = useTranslation("home");
    const { t: tSales } = useTranslation("home", { keyPrefix: "salesDashboard" });
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { salesManagerAgents, isLoading: agentsLoading } = useAgentTypes();
    const { activeCampaigns, activeTotalCount, sampledLeads, isLoading: isLoadingData, load } = useSalesDashboardData();

    useSetPageHeader(t("salesDashboard.headerTitle"), t("salesDashboard.headerSubtitle"), <HiOutlineBriefcase />);

    const [isNewCampaignModalOpen, setIsNewCampaignModalOpen] = useState(false);

    // The context caches the result after the first load, so switching to /recruitment and
    // back here won't re-fetch -- see SalesDashboardDataContext.
    useEffect(() => {
        load();
    }, [load]);

    // The agent id in this route is decorative (LeadsPage/LeadDetailPage fetch purely by
    // campaign/lead id), so any of the user's sales agents is a valid segment.
    const resolveAgentId = () => salesManagerAgents[0]?.id;

    const metrics = useMemo(() => {
        const now = dayjs();
        const newLeads = sampledLeads.filter(
            ({ lead }) => now.diff(dayjs(lead.createdAt), "day") < NEW_LEAD_WINDOW_DAYS
        ).length;
        const toReview = sampledLeads.filter(({ lead }) => lead.status === "New").length;
        const favourites = sampledLeads.filter(({ lead }) => lead.isFavourite).length;
        return { newLeads, toReview, favourites };
    }, [sampledLeads]);

    const recentLeads = useMemo(() =>
        [...sampledLeads]
            .sort((a, b) => new Date(b.lead.createdAt) - new Date(a.lead.createdAt))
            .slice(0, LEADS_TO_DISPLAY),
    [sampledLeads]);

    if (agentsLoading || isLoadingData) {
        return <DashboardSkeleton />;
    }

    if (salesManagerAgents.length === 0) {
        return (
            <VStack w="full" py={12} align="center" gap={4}>
                <Text color="gray.500">{tSales("emptyAgents")}</Text>
                <Button variant="primary" onClick={() => navigate("/models")}>
                    {tSales("emptyAgentsCta")}
                </Button>
            </VStack>
        );
    }

    const displayedCampaigns = activeCampaigns.slice(0, CAMPAIGNS_TO_DISPLAY);

    return (
        <VStack w="full" pt={2} pb={6} gap={4} align="flex-start">
            <HStack w="full" justify="space-between" align="flex-start" flexWrap="wrap" gap={3}>
                <VStack align="flex-start" gap={1}>
                    <Text fontSize="2xl" fontWeight={700} color="gray.900">
                        {t("recruitmentDashboard.greeting", { name: currentUser?.firstName ?? "" })}
                    </Text>
                    <Text color="gray.500" fontSize="sm">
                        {tSales("greetingSubtitle", { date: dayjs().format("D MMMM") })}
                    </Text>
                </VStack>
                <Button variant="primary" onClick={() => setIsNewCampaignModalOpen(true)}>
                    <HiOutlinePlus /> {tSales("newCampaignCta")}
                </Button>
            </HStack>

            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={3} w="full">
                <StatCard
                    label={tSales("activeCampaignsMetric")}
                    value={activeTotalCount}
                    icon={<HiOutlineCalendarDays />}
                    iconBg="#EDE9FE"
                    iconColor="#6D28D9"
                />
                <StatCard
                    label={tSales("newLeadsMetric")}
                    value={metrics.newLeads}
                    icon={<HiOutlineBriefcase />}
                    iconBg="#DCFCE7"
                    iconColor="#15803D"
                />
                <StatCard
                    label={tSales("toReviewMetric")}
                    value={metrics.toReview}
                    icon={<HiOutlineClock />}
                    iconBg="#FEF3C7"
                    iconColor="#B45309"
                />
                <StatCard
                    label={tSales("favouritesMetric")}
                    value={metrics.favourites}
                    icon={<HiOutlineStar />}
                    iconBg="#DBEAFE"
                    iconColor="#1D4ED8"
                />
            </SimpleGrid>

            <HStack w="full" align="flex-start" gap={4} flexWrap={{ base: "wrap", lg: "nowrap" }}>
                <Box flex="3" minW={{ base: "full", lg: "0" }} bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} overflow="hidden">
                    <HStack justify="space-between" px={4} py={3} borderBottom="1px solid" borderColor="gray.100">
                        <Text fontWeight={700} color="gray.900" fontSize="sm">{tSales("recentLeadsTitle")}</Text>
                    </HStack>
                    {recentLeads.length === 0 ? (
                        <Text color="gray.500" fontSize="sm" p={4}>{tSales("emptyRecentLeads")}</Text>
                    ) : (
                        <VStack gap={0} w="full" align="stretch">
                            {recentLeads.map(({ lead, campaign }) => (
                                <LeadRow
                                    key={lead.id}
                                    lead={lead}
                                    campaign={campaign}
                                    t={t}
                                    tSales={tSales}
                                    onClick={() => navigate(`/models/${resolveAgentId()}/campaigns/${campaign.id}/leads/${lead.id}`)}
                                />
                            ))}
                        </VStack>
                    )}
                </Box>

                <VStack flex="2" minW={{ base: "full", lg: "260px" }} gap={4} align="stretch">
                    <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} overflow="hidden">
                        <HStack justify="space-between" px={4} py={3} borderBottom="1px solid" borderColor="gray.100">
                            <Text fontWeight={700} color="gray.900" fontSize="sm">{tSales("activeCampaignsTitle")}</Text>
                            <Text as="button" color="purple.600" fontSize="xs" fontWeight={600} onClick={() => navigate("/models")} cursor="pointer">
                                {tSales("viewAll")}
                            </Text>
                        </HStack>
                        {displayedCampaigns.length === 0 ? (
                            <VStack align="flex-start" gap={2} p={4}>
                                <Text color="gray.500" fontSize="sm">{tSales("emptyCampaigns")}</Text>
                                <Button variant="outlined" size="sm" onClick={() => navigate("/models")}>
                                    {tSales("emptyCampaignsCta")}
                                </Button>
                            </VStack>
                        ) : (
                            <VStack gap={0} w="full" align="stretch">
                                {displayedCampaigns.map((campaign) => (
                                    <CampaignRow
                                        key={campaign.id}
                                        campaign={campaign}
                                        t={tSales}
                                        onClick={() => navigate(`/models/${resolveAgentId()}/campaigns/${campaign.id}/leads`)}
                                    />
                                ))}
                            </VStack>
                        )}
                    </Box>

                    <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={4}>
                        <Text fontWeight={700} color="gray.900" fontSize="sm" mb={3}>{tSales("agentsTitle")}</Text>
                        <HStack flexWrap="wrap" gap={2}>
                            {salesManagerAgents.map((agent) => (
                                <Text
                                    key={agent.id}
                                    as="button"
                                    onClick={() => navigate(`/models/${agent.id}`)}
                                    bg="gray.50"
                                    _hover={{ bg: "gray.100" }}
                                    color="gray.700"
                                    fontSize="xs"
                                    fontWeight={500}
                                    px={3}
                                    py={1.5}
                                    borderRadius="999px"
                                    cursor="pointer"
                                >
                                    {agent.name}
                                </Text>
                            ))}
                        </HStack>
                    </Box>
                </VStack>
            </HStack>

            <NewCampaignModal
                isOpen={isNewCampaignModalOpen}
                onClose={() => setIsNewCampaignModalOpen(false)}
            />
        </VStack>
    );
};

export default SalesDashboardPage;
